import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bold, Italic, Link as LinkIcon, Loader2, Check } from 'lucide-react';
import { auth } from '../../../firebase/firebase.config';
import { updateUserProfile } from '../../../firebase/user.service';
import useAppStore from '../../../store/appStore';

/**
 * Email Signature settings section.
 * Single live rich-text editor (contentEditable) — no separate preview mode.
 * What the user sees in the editor is exactly what will appear in emails.
 */
const InboxSection = ({ isMobile }) => {
    const [signature, setSignature] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const editorRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);

    // Zustand cache selectors
    const settingsEmailSignature = useAppStore((s) => s.settingsEmailSignature);
    const setSettingsEmailSignature = useAppStore((s) => s.setSettingsEmailSignature);
    const settingsProfile = useAppStore((s) => s.settingsProfile);

    // Load signature — prefer Zustand store (populated by AuthContext)
    useEffect(() => {
        // If we already have a cached signature, use it
        if (settingsEmailSignature !== null && settingsEmailSignature !== undefined) {
            setSignature(settingsEmailSignature);
            setLoading(false);
            return;
        }
        // Also check if settingsProfile has it
        if (settingsProfile?.emailSignature) {
            setSignature(settingsProfile.emailSignature);
            setSettingsEmailSignature(settingsProfile.emailSignature);
            setLoading(false);
            return;
        }

        // No signature available yet — just stop loading
        // (AuthContext will push profile data to store when ready)
        setLoading(false);
    }, [settingsEmailSignature, settingsProfile]); // eslint-disable-line

    // Sync signature HTML into the editor when it first loads
    useEffect(() => {
        if (!loading && editorRef.current && signature && !editorRef.current.innerHTML) {
            editorRef.current.innerHTML = signature;
            setIsEmpty(false);
        }
    }, [loading, signature]);

    const handleEditorInput = useCallback(() => {
        if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            setSignature(html);
            // Check if content is effectively empty (only whitespace / <br> / &nbsp;)
            const text = editorRef.current.innerText.trim();
            setIsEmpty(!text);
        }
    }, []);

    const execFormat = useCallback((command, value = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
        handleEditorInput();
    }, [handleEditorInput]);

    const handleBold = () => execFormat('bold');
    const handleItalic = () => execFormat('italic');

    const handleLink = () => {
        const url = prompt('Enter URL:');
        if (url) {
            execFormat('createLink', url);
        }
    };

    const handleSaveSignature = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        setSaving(true);
        setSaveResult(null);
        try {
            // If editor is empty, save empty string to clear the signature
            // Otherwise normalize: convert any raw newlines to <br> so the HTML
            // renders identically in email clients that don't support white-space:pre-wrap
            let signatureToSave = '';
            if (!isEmpty) {
                signatureToSave = signature
                    .replace(/\r\n/g, '<br>')
                    .replace(/\n/g, '<br>')
                    .replace(/\r/g, '<br>');
            }
            await updateUserProfile(userId, { emailSignature: signatureToSave });
            // Write-through: update Zustand cache so other views see fresh signature
            setSettingsEmailSignature(signatureToSave);
            setSaveResult({ success: true });
            setTimeout(() => setSaveResult(null), 3000);
        } catch (error) {
            console.error('Error saving email signature:', error);
            setSaveResult({ success: false, message: error.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="mb-4 md:mb-6">
                <h2 className="text-lg font-bold text-notion-text">Email Signature</h2>
                <p className="text-xs text-stone-500">
                    {isMobile
                        ? 'Your email sign-off.'
                        : 'Customize the signature appended to every outgoing email — manual, broadcast, and automation.'}
                </p>
            </div>

            <div className="bg-white border border-notion-border rounded-xl p-5 md:p-6 shadow-card">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    </div>
                ) : (
                    <>
                        {/* Formatting toolbar */}
                        <div className={`border border-notion-border rounded-t-lg p-2 bg-stone-50 flex ${isMobile ? 'gap-4 justify-center' : 'gap-2'}`}>
                            <button
                                type="button"
                                onClick={handleBold}
                                className="hover:bg-stone-200 rounded p-1.5 transition"
                                title="Bold"
                            >
                                <Bold className={`${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-stone-600`} />
                            </button>
                            <button
                                type="button"
                                onClick={handleItalic}
                                className="hover:bg-stone-200 rounded p-1.5 transition"
                                title="Italic"
                            >
                                <Italic className={`${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-stone-600`} />
                            </button>
                            <button
                                type="button"
                                onClick={handleLink}
                                className="hover:bg-stone-200 rounded p-1.5 transition"
                                title="Insert Link"
                            >
                                <LinkIcon className={`${isMobile ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-stone-600`} />
                            </button>
                        </div>

                        {/* Live rich-text editor — acts as both editor and preview */}
                        <div className="relative">
                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={handleEditorInput}
                                onFocus={() => setIsEmpty(!(editorRef.current?.innerText.trim()))}
                                className={`w-full min-h-[160px] bg-white border border-t-0 border-notion-border rounded-b-lg p-4 ${isMobile ? 'text-sm' : 'text-xs'} text-stone-700 leading-relaxed outline-none focus:ring-1 focus:ring-stone-300 transition overflow-auto`}
                                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            />
                            {/* Placeholder overlay — shown only when editor is empty */}
                            {isEmpty && (
                                <div
                                    className={`absolute top-0 left-0 right-0 p-4 pointer-events-none ${isMobile ? 'text-sm' : 'text-xs'} text-stone-400 leading-relaxed select-none`}
                                >
                                    Write your email signature here…
                                </div>
                            )}
                        </div>

                        {/* Save button + status */}
                        <div className="flex items-center gap-3 mt-4">
                            <button
                                onClick={handleSaveSignature}
                                disabled={saving}
                                className={`${isMobile ? 'w-full' : ''} bg-stone-800 text-white px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded-lg ${isMobile ? 'text-sm' : 'text-xs'} font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-1.5 transition`}
                            >
                                {saving ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : null}
                                {saving ? 'Saving...' : 'Save Signature'}
                            </button>

                            {saveResult && (
                                <span className={`text-xs flex items-center gap-1 ${saveResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {saveResult.success ? (
                                        <><Check className="w-3 h-3" /> Saved</>
                                    ) : (
                                        saveResult.message || 'Failed to save'
                                    )}
                                </span>
                            )}
                        </div>

                        {/* Helper tip */}
                        <p className="text-[10px] text-stone-400 mt-3">
                            Tip: Your signature is automatically appended to every outgoing email — including manual sends, broadcasts, and automations.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default InboxSection;
